import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate, getInitials, getStatusColor } from '@/lib/utils'
import {
  Search, Plus, Inbox, X, Check, Circle, CheckCircle2,
  Clock, AlertTriangle, Tag, User, Users, ChevronDown,
  Filter, MoreHorizontal, Bell, Archive, Trash2,
  Mail, MessageSquare, Phone, Smartphone, Globe,
  Star, Zap, RefreshCw, SlidersHorizontal,
} from 'lucide-react'
import { toast } from 'sonner'
import Avatar from '@/components/Avatar'
import SearchInboxModal from './SearchInboxModal'

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  email: <Mail style={{ width: 11, height: 11 }} />,
  chat: <MessageSquare style={{ width: 11, height: 11 }} />,
  phone: <Phone style={{ width: 11, height: 11 }} />,
  sms: <Smartphone style={{ width: 11, height: 11 }} />,
  portal: <Globe style={{ width: 11, height: 11 }} />,
}

const CHANNEL_COLORS: Record<string, string> = {
  email:  'var(--info)',
  chat:   'var(--accent)',
  phone:  'var(--positive)',
  sms:    'var(--warning)',
  portal: 'var(--danger)',
}

const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
  urgent: { color: 'var(--danger)',       label: 'Urgent' },
  high:   { color: 'var(--warning)',      label: 'High' },
  normal: { color: 'var(--accent)',       label: 'Normal' },
  low:    { color: 'var(--ink-tertiary)', label: 'Low' },
}

const INBOX_SECTIONS = [
  { id: 'mine', label: 'Mine', icon: User },
  { id: 'unassigned', label: 'Unassigned', icon: Circle },
  { id: 'all', label: 'All', icon: Inbox },
  { id: 'team', label: 'Team', icon: Users },
]

const STATUS_TABS = [
  { id: 'open', label: 'Open' },
  { id: 'pending', label: 'Pending' },
  { id: 'snoozed', label: 'Snoozed' },
  { id: 'resolved', label: 'Resolved' },
]

function NewConversationModal({ onClose, onCreated }: any) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({ contact_name: '', contact_email: '', subject: '', channel: 'email', priority: 'normal' })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleSave = async () => {
    if (!form.contact_name.trim()) { toast.error('Contact name is required'); return }
    setSaving(true)
    const { error } = await supabase.from('conversations').insert({
      ...form, status: 'open', unread_count: 0, workspace_id: workspace!.id,
    })
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Conversation created')
    onCreated()
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New conversation</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact name *</label>
            <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Alex Johnson" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
            <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="alex@company.com" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</label>
            <input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="What's this about?" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Channel</label>
              <select value={form.channel} onChange={e => set('channel', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {['email', 'chat', 'phone', 'sms', 'portal'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {['low', 'normal', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Creating...' : 'Create conversation'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function SLATimer({ createdAt, status }: { createdAt: string; status: string }) {
  if (status !== 'open') return null
  const created = new Date(createdAt)
  const now = new Date()
  const hoursOld = (now.getTime() - created.getTime()) / (1000 * 60 * 60)
  const slaHours = 24
  const remaining = slaHours - hoursOld
  const pct = Math.max(0, Math.min(100, (remaining / slaHours) * 100))
  const color = remaining < 2 ? 'var(--danger)' : remaining < 6 ? 'var(--warning)' : 'var(--positive)'

  if (hoursOld > slaHours) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <AlertTriangle style={{ width: 10, height: 10, color: 'var(--danger)' }} />
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--danger)' }}>SLA breached</span>
    </div>
  )

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div style={{ width: 28, height: 3, background: 'var(--border-default)', borderRadius: 100, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 100 }} />
      </div>
      <span style={{ fontSize: 10, color, fontWeight: 600 }}>
        {remaining < 1 ? `${Math.round(remaining * 60)}m` : `${Math.round(remaining)}h`}
      </span>
    </div>
  )
}

export default function InboxPage() {
  const { workspace, profile } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [section, setSection] = useState('all')
  const [statusTab, setStatusTab] = useState('open')
  const [search, setSearch] = useState('')
  const [showNewModal, setShowNewModal] = useState(false)
  const [showMessageSearch, setShowMessageSearch] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showFilters, setShowFilters] = useState(false)
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [channelFilter, setChannelFilter] = useState('all')
  const [showBulkMenu, setShowBulkMenu] = useState(false)

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['conversations', workspace?.id, section, statusTab],
    queryFn: async () => {
      if (!workspace?.id) return []
      let query = supabase.from('conversations').select('*').eq('workspace_id', workspace.id)

      if (statusTab === 'snoozed') {
        query = query.eq('is_snoozed', true)
      } else {
        query = query.eq('status', statusTab).eq('is_snoozed', false)
      }

      if (section === 'mine' && profile?.id) {
        query = query.eq('assigned_to', profile.id)
      } else if (section === 'unassigned') {
        query = query.is('assigned_to', null)
      }

      const { data, error } = await query.order('updated_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    refetchInterval: 30000,   // slow safety fallback — realtime (below) is the live path
  })

  // ─── Realtime: conversations list (Pattern B — invalidate on any change) ──
  // inbox-receive UPDATEs the conversation row on every inbound (last_message /
  // last_message_at), so a conversations-sub alone keeps the list live (new
  // threads, status/assignment, preview). RLS scopes events to this workspace.
  useEffect(() => {
    if (!workspace?.id) return
    const channel = supabase
      .channel(`inbox-list:${workspace.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'conversations',
        filter: `workspace_id=eq.${workspace.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['conversations', workspace.id] })
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [workspace?.id, queryClient])

  const { data: agents = [] } = useQuery({
    queryKey: ['agents', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name, role').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  // Batched normalized-tag map for the visible conversations. One round-trip
  // keyed off the current conversation list; grouped client-side so each row
  // can render its chips without N+1 queries.
  const conversationIds = useMemo(() => conversations.map((c: any) => c.id), [conversations])
  const { data: tagMap = {} } = useQuery<Record<string, Array<{ id: string; name: string; color: string }>>>({
    queryKey: ['conversation-tags-map', workspace?.id, conversationIds.join(',')],
    queryFn: async () => {
      if (conversationIds.length === 0) return {}
      const { data, error } = await supabase
        .from('conversation_tags')
        .select('conversation_id, tag:tags(id, name, color)')
        .in('conversation_id', conversationIds)
      if (error) return {}
      // supabase codegen types FK joins as arrays even when the relation is
      // one-to-one; runtime shape is `{ conversation_id, tag: TagRow | null }`.
      const rows = (data ?? []) as unknown as Array<{
        conversation_id: string
        tag: { id: string; name: string; color: string } | null
      }>
      const map: Record<string, Array<{ id: string; name: string; color: string }>> = {}
      for (const row of rows) {
        if (!row.tag) continue
        ;(map[row.conversation_id] ??= []).push(row.tag)
      }
      return map
    },
    enabled: conversationIds.length > 0,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[]; updates: any }) => {
      const { error } = await supabase.from('conversations').update(updates).in('id', ids)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['conversations'] }); setSelected(new Set()) },
    onError: () => toast.error('Failed to update'),
  })

  const counts = useMemo(() => ({
    open: conversations.filter((c: any) => c.status === 'open' && !c.is_snoozed).length,
    pending: conversations.filter((c: any) => c.status === 'pending').length,
    snoozed: conversations.filter((c: any) => c.is_snoozed).length,
    resolved: conversations.filter((c: any) => c.status === 'resolved').length,
    unread: conversations.filter((c: any) => c.unread_count > 0).length,
  }), [conversations])

  const filtered = useMemo(() => {
    let list = [...conversations]
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((c: any) => c.contact_name?.toLowerCase().includes(q) || c.subject?.toLowerCase().includes(q) || c.contact_email?.toLowerCase().includes(q))
    }
    if (priorityFilter !== 'all') list = list.filter((c: any) => c.priority === priorityFilter)
    if (channelFilter !== 'all') list = list.filter((c: any) => c.channel === channelFilter)
    // Sort: urgent first, then by updated_at
    list.sort((a: any, b: any) => {
      const priorityOrder: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 }
      const pDiff = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2)
      if (pDiff !== 0) return pDiff
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    })
    return list
  }, [conversations, search, priorityFilter, channelFilter])

  const toggleSelect = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleAll = () => selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map((c: any) => c.id)))

  const bulkAction = (action: string) => {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    switch (action) {
      case 'resolve': updateMutation.mutate({ ids, updates: { status: 'resolved' } }); toast.success(`${ids.length} resolved`); break
      case 'pending': updateMutation.mutate({ ids, updates: { status: 'pending' } }); toast.success(`${ids.length} marked pending`); break
      case 'reopen': updateMutation.mutate({ ids, updates: { status: 'open' } }); toast.success(`${ids.length} reopened`); break
      case 'snooze': updateMutation.mutate({ ids, updates: { is_snoozed: true, snoozed_until: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() } }); toast.success(`${ids.length} snoozed for 24h`); break
    }
    setShowBulkMenu(false)
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
        {/* Heading + action buttons. Wraps on narrow viewports so the
            New/Filters/Refresh buttons drop below the title instead of
            overflowing the toolbar. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Inbox</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {counts.open} open · {counts.unread > 0 ? `${counts.unread} unread · ` : ''}{conversations.length} total
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => queryClient.invalidateQueries({ queryKey: ['conversations'] })}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, color: 'var(--ink-secondary)', fontSize: 12, cursor: 'pointer' }}>
              <RefreshCw style={{ width: 13, height: 13 }} />
            </button>
            <button onClick={() => setShowMessageSearch(true)} title="Search message content"
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, color: 'var(--ink-secondary)', fontSize: 12, cursor: 'pointer' }}>
              <Search style={{ width: 13, height: 13 }} /> Messages
            </button>
            <button onClick={() => setShowFilters(!showFilters)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', background: showFilters ? 'var(--accent-dim)' : 'var(--hover-bg)', border: showFilters ? '1px solid var(--accent-border)' : '1px solid var(--border-subtle)', borderRadius: 9, color: showFilters ? 'var(--accent-text)' : 'var(--ink-secondary)', fontSize: 12, cursor: 'pointer' }}>
              <SlidersHorizontal style={{ width: 13, height: 13 }} /> Filters
            </button>
            <button onClick={() => setShowNewModal(true)} className="btn-primary">
              <Plus style={{ width: 15, height: 15 }} /> New
            </button>
          </div>
        </div>

        {/* Section tabs — wrap on narrow viewports rather than overflow. */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 12, flexWrap: 'wrap' }}>
          {INBOX_SECTIONS.map(s => (
            <button key={s.id} onClick={() => setSection(s.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 9, fontSize: 13, fontWeight: section === s.id ? 600 : 400, cursor: 'pointer', background: section === s.id ? 'var(--accent-dim)' : 'transparent', color: section === s.id ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: section === s.id ? '1px solid var(--accent-border)' : '1px solid transparent', transition: 'all 150ms ease' }}>
              <s.icon style={{ width: 13, height: 13 }} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Status tabs — wrap on narrow viewports rather than overflow. */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', marginBottom: 0, flexWrap: 'wrap' }}>
          {STATUS_TABS.map(tab => {
            const count = counts[tab.id as keyof typeof counts]
            return (
              <button key={tab.id} onClick={() => setStatusTab(tab.id)}
                style={{ padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: statusTab === tab.id ? 600 : 400, color: statusTab === tab.id ? 'var(--ink-primary)' : 'var(--ink-tertiary)', borderBottom: statusTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent', transition: 'all 150ms ease', display: 'flex', alignItems: 'center', gap: 6 }}>
                {tab.label}
                {count > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 100, background: statusTab === tab.id ? 'var(--accent-dim)' : 'var(--hover-bg)', color: statusTab === tab.id ? 'var(--accent-text)' : 'var(--ink-tertiary)' }}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Search + filters */}
      <div style={{ padding: '12px 24px', flexShrink: 0, borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Select all */}
          <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll}
            style={{ width: 14, height: 14, cursor: 'pointer', accentColor: 'var(--accent)', flexShrink: 0 }} />

          {/* Search */}
          <div style={{ position: 'relative', flex: 1 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search conversations..."
              style={{ width: '100%', padding: '8px 12px 8px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>

          {/* Bulk actions */}
          {selected.size > 0 && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowBulkMenu(!showBulkMenu)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {selected.size} selected <ChevronDown style={{ width: 12, height: 12 }} />
              </button>
              <AnimatePresence>
                {showBulkMenu && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                    style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 160 }}>
                    {[
                      { action: 'resolve', label: '✓ Resolve',        color: 'var(--positive)' },
                      { action: 'pending', label: '⏳ Mark pending',   color: 'var(--warning)' },
                      { action: 'reopen',  label: '↩ Reopen',          color: 'var(--accent)' },
                      { action: 'snooze',  label: '😴 Snooze 24h',     color: 'var(--info)' },
                    ].map(item => (
                      <button key={item.action} onClick={() => bulkAction(item.action)}
                        style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: item.color, textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        {item.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Expanded filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.15 }}
              style={{ overflow: 'hidden', marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Priority</p>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {['all', 'urgent', 'high', 'normal', 'low'].map(p => (
                      <button key={p} onClick={() => setPriorityFilter(p)}
                        style={{ padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: priorityFilter === p ? 'var(--accent)' : 'var(--hover-bg)', color: priorityFilter === p ? 'var(--accent-ink)' : PRIORITY_CONFIG[p]?.color || 'var(--ink-tertiary)', border: priorityFilter === p ? 'none' : '1px solid var(--border-subtle)' }}>
                        {p === 'all' ? 'All' : p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Channel</p>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {['all', 'email', 'chat', 'phone', 'sms', 'portal'].map(c => (
                      <button key={c} onClick={() => setChannelFilter(c)}
                        style={{ padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: channelFilter === c ? 'var(--accent)' : 'var(--hover-bg)', color: channelFilter === c ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: channelFilter === c ? 'none' : '1px solid var(--border-subtle)' }}>
                        {c === 'all' ? 'All' : c}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Conversation list */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center' }}>
            <Inbox style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
              {search ? 'No conversations match your search' : statusTab === 'open' ? 'All caught up! 🎉' : `No ${statusTab} conversations`}
            </p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
              {search ? 'Try a different search' : 'New conversations will appear here'}
            </p>
            {!search && statusTab === 'open' && (
              <button onClick={() => setShowNewModal(true)} className="btn-primary" style={{ margin: '0 auto' }}>
                <Plus style={{ width: 14, height: 14 }} /> New conversation
              </button>
            )}
          </div>
        ) : (
          filtered.map((conv: any, i: number) => {
            const isSelected = selected.has(conv.id)
            const priorityCfg = PRIORITY_CONFIG[conv.priority] || PRIORITY_CONFIG.normal
            const channelColor = CHANNEL_COLORS[conv.channel] || 'var(--accent)'
            const isUnread = conv.unread_count > 0

            return (
              <motion.div key={conv.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '13px 24px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', background: isSelected ? 'var(--accent-dim)' : 'transparent', transition: 'background 150ms ease', position: 'relative' }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--hover-bg)' }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}>

                {/* Priority bar */}
                {conv.priority !== 'normal' && conv.priority !== 'low' && (
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: priorityCfg.color, borderRadius: '0 2px 2px 0' }} />
                )}

                {/* Checkbox */}
                <div onClick={e => { e.stopPropagation(); toggleSelect(conv.id) }} style={{ paddingTop: 2, flexShrink: 0 }}>
                  <input type="checkbox" checked={isSelected} onChange={() => {}}
                    style={{ width: 14, height: 14, cursor: 'pointer', accentColor: 'var(--accent)' }} />
                </div>

                {/* Unread dot */}
                <div style={{ paddingTop: 6, flexShrink: 0 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: isUnread ? 'var(--accent)' : 'transparent', boxShadow: isUnread ? '0 0 6px color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)' : 'none', transition: 'all 150ms ease' }} />
                </div>

                {/* Avatar */}
                <Avatar name={conv.contact_name || 'U'} size={36} />

                {/* Content */}
                <div style={{ flex: 1, overflow: 'hidden' }} onClick={() => navigate(`/app/inbox/${conv.id}`)}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, overflow: 'hidden' }}>
                      <span style={{ fontSize: 13, fontWeight: isUnread ? 700 : 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.contact_name || 'Unknown'}
                      </span>
                      {/* Channel badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 100, background: `color-mix(in srgb, ${channelColor} var(--status-tint-fill), transparent)`, border: `1px solid color-mix(in srgb, ${channelColor} var(--status-tint-strong), transparent)`, flexShrink: 0 }}>
                        <span style={{ color: channelColor }}>{CHANNEL_ICONS[conv.channel]}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, color: channelColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{conv.channel}</span>
                      </div>
                      {/* Priority badge */}
                      {conv.priority === 'urgent' && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--danger)', background: 'color-mix(in srgb, var(--danger) var(--status-tint-fill), transparent)', border: '1px solid var(--danger-border)', padding: '2px 6px', borderRadius: 100, flexShrink: 0, textTransform: 'uppercase' }}>URGENT</span>
                      )}
                      {/* Snoozed badge */}
                      {conv.is_snoozed && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--info)', background: 'color-mix(in srgb, var(--info) var(--status-tint-fill), transparent)', border: '1px solid var(--info-border)', padding: '2px 6px', borderRadius: 100, flexShrink: 0 }}>😴 SNOOZED</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {isUnread && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 100, background: 'var(--accent)', color: 'var(--accent-ink)' }}>
                          {conv.unread_count}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(conv.updated_at)}</span>
                    </div>
                  </div>

                  <p style={{ fontSize: 12, fontWeight: isUnread ? 600 : 400, color: isUnread ? 'var(--ink-secondary)' : 'var(--ink-tertiary)', margin: '0 0 5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {conv.subject || 'No subject'}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {conv.last_message || 'No messages yet'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 8 }}>
                      <SLATimer createdAt={conv.created_at} status={conv.status} />
                      {conv.assigned_name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {/* Fixed-purple assigned-agent badge — non-theming chrome (operator pattern: agent = always purple) */}
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: 'var(--accent-ink)' }}>
                            {getInitials(conv.assigned_name)}
                          </div>
                          <span style={{ fontSize: 10, color: 'var(--ink-tertiary)' }}>{conv.assigned_name.split(' ')[0]}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tags — normalized. t.color is DB user content; runtime alpha computation grandfathered */}
                  {(tagMap[conv.id]?.length ?? 0) > 0 && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap' }}>
                      {tagMap[conv.id].map(t => {
                        const m = /^#([0-9a-f]{6})$/i.exec(t.color)
                        const n = m ? parseInt(m[1], 16) : 0x7c3aed
                        const rgb = `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`
                        return (
                          <span key={t.id} style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 100, background: `rgba(${rgb},0.12)`, color: t.color, border: `1px solid rgba(${rgb},0.2)` }}>
                            {t.name}
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Quick actions on hover */}
                <div className="quick-actions" style={{ display: 'flex', gap: 4, flexShrink: 0, opacity: 0, transition: 'opacity 150ms ease' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
                  {conv.status === 'open' && (
                    <button onClick={e => { e.stopPropagation(); updateMutation.mutate({ ids: [conv.id], updates: { status: 'resolved' } }); toast.success('Resolved') }}
                      title="Resolve"
                      style={{ width: 28, height: 28, borderRadius: 8, background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid var(--positive-border)', cursor: 'pointer', color: 'var(--positive)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check style={{ width: 12, height: 12 }} />
                    </button>
                  )}
                  <button onClick={e => { e.stopPropagation(); updateMutation.mutate({ ids: [conv.id], updates: { is_snoozed: !conv.is_snoozed, snoozed_until: !conv.is_snoozed ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : null } }); toast.success(conv.is_snoozed ? 'Unsnoozed' : 'Snoozed for 24h') }}
                    title={conv.is_snoozed ? 'Unsnooze' : 'Snooze'}
                    style={{ width: 28, height: 28, borderRadius: 8, background: 'color-mix(in srgb, var(--info) var(--status-tint-dim), transparent)', border: '1px solid var(--info-border)', cursor: 'pointer', color: 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Clock style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              </motion.div>
            )
          })
        )}
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewConversationModal
            onClose={() => setShowNewModal(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['conversations'] })}
          />
        )}
        {showMessageSearch && (
          <SearchInboxModal
            onClose={() => setShowMessageSearch(false)}
            onSelect={id => { setShowMessageSearch(false); navigate(`/app/inbox/${id}`) }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}